import { environment } from '@/environment';
import { Password } from '@/models/password';
import { UserData } from '@/models/user';
import { UpdateInvite } from '@/models/updateInvite';
import { Invitation } from '@/models/invitation';
import { InviteToAccept } from '@/models/inviteToAccept';
import { SignUpState } from '@/store/types';
import { SignUpErrors } from '../store/modules/signup/types';
import axios, { AxiosError, AxiosResponse } from 'axios';
import store from '../store';

export default {
  url: `${environment.api}/users`,
  headers: {
    Authorization: '',
  },

  post(
    userData: SignUpState,
    invitation: string
  ): Promise<UserData | SignUpErrors> {
    let url = this.url;

    if (invitation.length > 0) {
      url += '?invitation=' + invitation;
    }

    return axios
      .post(url, userData)
      .then((response: AxiosResponse<UserData>) => {
        return Promise.resolve(response.data);
      })
      .catch((error: AxiosError) => {
        return Promise.reject(error.response?.data);
      });
  },

  put(userData: UserData): Promise<UserData> {
    this.headers.Authorization = store.getters['auth/getAuthorization'];

    return axios
      .put(`${this.url}/${userData.uuid}`, userData, { headers: this.headers })
      .then((response: AxiosResponse<UserData>) => {
        return Promise.resolve(response.data);
      })
      .catch((error: AxiosError) => {
        return Promise.reject(error.message);
      });
  },

  putNewPassword(password: Password): Promise<void> {
    this.headers.Authorization = store.getters['auth/getAuthorization'];

    return axios
      .put(
        `${this.url}/${store.getters['auth/getUserData'].uuid}/password`,
        password,
        { headers: this.headers }
      )
      .then((response: AxiosResponse<void>) => {
        return Promise.resolve(response.data);
      })
      .catch((error: AxiosError) => {
        return Promise.reject(error.message);
      });
  },

  getInvitations(): Promise<Invitation[]> {
    return axios
      .get<Invitation[]>(
        `${this.url}/${store.getters['auth/getUserData'].uuid}/invitations`,
        { headers: { Authorization: store.getters['auth/getAuthorization'] } }
      )
      .then((response: AxiosResponse<Invitation[]>) => {
        return Promise.resolve(response.data);
      })
      .catch((error: AxiosError) => {
        return Promise.reject(error.message);
      });
  },

  getInvitation(invitation: InviteToAccept): Promise<Invitation> {
    return axios
      .get<Invitation>(
        `${this.url}/${invitation.inviterUuid}/invitations/${invitation.invitationUuid}`,
        { headers: this.headers }
      )
      .then((response: AxiosResponse<Invitation>) => {
        return Promise.resolve(response.data);
      })
      .catch((error: AxiosError) => {
        return Promise.reject(error.message);
      });
  },

  postInvitation(): Promise<Invitation> {
    return axios
      .post<Invitation>(
        `${this.url}/${store.getters['auth/getUserData'].uuid}/invitations?organization=${store.getters['org/getShortName']}`,
        store.getters['profile/sharing/invite'],
        { headers: { Authorization: store.getters['auth/getAuthorization'] } }
      )
      .then((response: AxiosResponse<Invitation>) => {
        return Promise.resolve(response.data);
      })
      .catch((error: AxiosError) => {
        return Promise.reject(error.message);
      });
  },

  acceptInvitation(inviteToAccept: InviteToAccept): Promise<void> {
    const invitation: UpdateInvite = {
      state: 'ACCEPTED',
    };

    return axios
      .put(
        `${this.url}/${inviteToAccept.inviterUuid}/invitations/${inviteToAccept.invitationUuid}`,
        invitation,
        { headers: { Authorization: store.getters['auth/getAuthorization'] } }
      )
      .then((response: AxiosResponse<void>) => {
        return Promise.resolve(response.data);
      })
      .catch((error: AxiosError) => {
        return Promise.reject(error.message);
      });
  },

  deleteInvitation(): Promise<Invitation> {
    return axios
      .delete<Invitation>(
        `${this.url}/${store.getters['auth/getUserData'].uuid}/invitations/${store.getters['profile/sharing/selected'].uuid}`,
        { headers: { Authorization: store.getters['auth/getAuthorization'] } }
      )
      .then((response: AxiosResponse<Invitation>) => {
        return Promise.resolve(response.data);
      })
      .catch((error: AxiosError) => {
        return Promise.reject(error.message);
      });
  },
};
